import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * SettingsContext
 * 
 * Provides restaurant and group selection state across all pages.
 * Persists selection in localStorage.
 * Supports multi-group access for group-admin users.
 */

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  // Location selection state
  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('yapn_selected_restaurant');
    return saved || 'all';
  });

  // Group selection state for multi-group users
  const [selectedGroupId, setSelectedGroupIdState] = useState(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('yapn_selected_group');
    return saved || null;
  });

  // Available groups for the current user (set by AuthenticatedApp)
  const [availableGroups, setAvailableGroups] = useState([]);

  // Save location to localStorage whenever selection changes
  useEffect(() => {
    if (selectedLocationId) {
      localStorage.setItem('yapn_selected_restaurant', selectedLocationId);
    }
  }, [selectedLocationId]);

  // Save group to localStorage whenever selection changes
  useEffect(() => {
    if (selectedGroupId) {
      localStorage.setItem('yapn_selected_group', selectedGroupId);
    }
  }, [selectedGroupId]);

  // Set selected group (locations remain unchanged - all locations always visible)
  const setSelectedGroupId = useCallback((groupId) => {
    setSelectedGroupIdState(groupId);
  }, []);

  // Format group ID to display name
  const formatGroupName = useCallback((groupId) => {
    if (!groupId) return '';
    return groupId
      .replace(/_/g, ' ')
      .replace(/GROUP/gi, '')
      .trim()
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }, []);

  const value = {
    // Location selection
    selectedLocationId,
    setSelectedLocationId,
    
    // Group selection (multi-group support)
    selectedGroupId,
    setSelectedGroupId,
    availableGroups,
    setAvailableGroups,
    
    // Utility functions
    formatGroupName,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

